import * as Sentry from '@sentry/react';
import { queryClientAtom } from 'jotai-tanstack-query';
import { fetchClientAtom } from '@/atoms/queries/client';
import { userDefaultTeamIdsAtom } from '@/atoms/queries/teams';
import { FetchError } from '@/query/FetchError';
import type { Getter } from 'jotai';

const HOME_STALE_TIME_MS = 5 * 60 * 1000;

// Looks something up for the user's default team, validating the response with `parse` (which throws on a shape the
// UI can't use). No default team and a 404 (nothing to show) give null. Any other FetchError is reported and also
// gives null, so the one card that needed it is left out; anything else, such as a response that fails `parse`,
// rejects, and Quick Start reports it and leaves the card out.
export const fetchForDefaultTeam = async <T>(
  get: Getter,
  queryName: string,
  path: (teamId: number) => string,
  parse: (response: unknown) => T,
): Promise<T | null> => {
  const { fetch } = get(fetchClientAtom);
  const queryClient = get(queryClientAtom);
  const teamId = (await get(userDefaultTeamIdsAtom))?.datahubId;
  if (!teamId) return null;

  try {
    return await queryClient.fetchQuery({
      queryKey: [queryName, teamId],
      staleTime: HOME_STALE_TIME_MS,
      // Quick Start waits for every lookup, so retrying one would hold the whole section on its loader; a failure
      // only leaves out one card.
      retry: false,
      queryFn: async (): Promise<T | null> => {
        try {
          return parse(await fetch(path(teamId)));
        } catch (error) {
          if (error instanceof FetchError && error.status === 404) return null;
          throw error;
        }
      },
    });
  } catch (error) {
    if (!(error instanceof FetchError)) throw error;
    Sentry.captureException(error);
    return null;
  }
};
